import React, { useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import {
  scrollToTop,
  orcaflo_hero1,
  orcaflo_hero2,
  orcaflo_hero1_mob,
  orcaflo_hero2_mob,
  orcaflo_logo,
} from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import HomePageFooter from "src/views/home/components/Footer";
import Footer from "src/components/footer";

// Orca mascot SVG with vibrant iridescent gradient and unique instance ID
export const OrcaMascot: React.FC<{ size?: number; className?: string; id?: string }> = ({
  size = 180,
  className = "",
  id = "default",
}) => {
  const gradId = `orcaGrad_${id}`;
  const glowId = `orcaGlow_${id}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`orca-mascot-svg ${className}`}
    >
      <defs>
        <linearGradient id={gradId} x1="20" y1="10" x2="140" y2="150" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#00d2ff" />
          <stop offset="35%" stopColor="#3b82f6" />
          <stop offset="70%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
        <filter id={glowId} x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#8b5cf6" floodOpacity="0.28" />
        </filter>
      </defs>
      
      <g filter={`url(#${glowId})`}>
        {/* Curved outer body of Orca / Whale */}
        <path
          d="M 68 22 
             C 92 12, 126 24, 134 52 
             C 142 78, 128 102, 108 116 
             C 96 125, 88 135, 94 148
             C 86 142, 80 134, 82 124
             C 84 116, 92 108, 98 100
             C 114 82, 118 64, 106 44
             C 96 28, 76 30, 68 36
             C 54 44, 44 60, 44 78
             C 44 94, 52 108, 66 118
             C 68 120, 70 126, 66 128
             C 60 130, 52 122, 46 114
             C 32 98, 28 76, 36 54
             C 42 38, 52 28, 68 22 Z"
          stroke={`url(#${gradId})`}
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="rgba(255, 255, 255, 0.05)"
        />
        
        {/* Fin flipper detail */}
        <path
          d="M 72 74 C 84 76, 96 86, 98 96 C 90 94, 80 88, 72 74 Z"
          fill={`url(#${gradId})`}
        />
        
        {/* Fluke / Tail curve */}
        <path
          d="M 94 148 C 102 142, 112 142, 116 146 C 110 148, 102 152, 94 148 Z"
          fill={`url(#${gradId})`}
        />

        {/* Eye dot */}
        <circle cx="118" cy="62" r="3.5" fill="#3b82f6" />
      </g>
    </svg>
  );
};

const OrcaFlo: React.FC = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  const featureCards = [
    {
      num: "1.",
      title: "One-Click Ticket Creation:",
      desc: "Create tickets instantly with no multi-step forms slowing teams down.",
      bullets: [
        "Single-click ticket creation from any view.",
        "Minimal fields, maximum speed — ready for teams to start immediately.",
      ],
    },
    {
      num: "2.",
      title: "Waterfall & Scrum, In One Tool:",
      desc: "Manage projects using the methodology that fits — without switching platforms.",
      bullets: [
        "Different client teams and business units can share methodology.",
        "Teams can run Sprint and Waterfall projects side-by-side in one workspace.",
      ],
    },
    {
      num: "3.",
      title: "Kanban & Scrum Boards:",
      desc: "Visualize and manage work the way your team plays it.",
      bullets: [
        "Drag-and-drop task management.",
        "Sprint planning and backlog grooming built in.",
      ],
    },
    {
      num: "4.",
      title: "Pre-Built Dashboards:",
      desc: "Get visibility into project health the moment you set up a project — no dashboard building required.",
      bullets: [
        "Ready-to-use views for velocity, workload, and status.",
        "No configuration or third-party BI tools needed.",
      ],
    },
    {
      num: "5.",
      title: "Intuitive, No-Training-Needed UI:",
      desc: "Designed so non-technical teams can get started without hand-holding.",
      bullets: [
        "Clean, quiet interface built for fast onboard folding.",
        "No IT-led training required to use any part of the platform.",
      ],
    },
    {
      num: "6.",
      title: "Timesheets Without Integrations:",
      desc: "Log and track time natively within the platform.",
      bullets: [
        "No third-party time-tracking tools required.",
        "Time data ties directly back to tasks and sprints.",
      ],
    },
    {
      num: "7.",
      title: "Daily Standup Tracker:",
      desc: "Keep teams aligned with a structured, built-in standup flow.",
      bullets: [
        "Asynchronous standup inputs.",
        "Blockers and updates visible to the whole team.",
      ],
    },
    {
      num: "8.",
      title: "Real-Time, Fully Customized Notifications:",
      desc: "Stay updated wherever your team already works.",
      bullets: [
        "Native integrations for Slack, email, and in-app.",
        "Fully customizable by user, project, and event type.",
      ],
    },
    {
      num: "9.",
      title: "Multiple Views & Customizable Portal:",
      desc: "View your work the way that makes sense to you on a portal shaped around your workflow.",
      bullets: [
        "Gantt, Calendar, and List views alongside boards.",
        "Configurable portal to match team structure and process.",
      ],
    },
  ];

  const flowCards = [
    {
      id: "card-1",
      num: "01",
      title: "Create Tickets",
      desc: "Teams create tickets in a single click.",
      tag: "blue",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </svg>
      ),
    },
    {
      id: "card-2",
      num: "02",
      title: "AI Breaks It Down",
      desc: "The AI Strategist Agent converts tickets into epics, tasks, and subtasks with acceptance criteria and task-level detail.",
      tag: "purple",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <line x1="9" y1="1" x2="9" y2="4" />
          <line x1="15" y1="1" x2="15" y2="4" />
          <line x1="9" y1="20" x2="9" y2="23" />
          <line x1="15" y1="20" x2="15" y2="23" />
          <line x1="20" y1="9" x2="23" y2="9" />
          <line x1="20" y1="14" x2="23" y2="14" />
          <line x1="1" y1="9" x2="4" y2="9" />
          <line x1="1" y1="14" x2="4" y2="14" />
        </svg>
      ),
    },
    {
      id: "card-3",
      num: "03",
      title: "Choose Your Method",
      desc: "Manage work in Waterfall or Scrum, each with tailored screens and workflows.",
      tag: "green",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="6" y1="3" x2="6" y2="15" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M18 9a9 9 0 0 1-9 9" />
        </svg>
      ),
    },
    {
      id: "card-4",
      num: "04",
      title: "Track Progress",
      desc: "Stay on top with daily standups, pre-built dashboards, and views like Gantt, Calendar, and List.",
      tag: "amber",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="15" y1="3" x2="15" y2="21" />
        </svg>
      ),
    },
    {
      id: "card-5",
      num: "05",
      title: "Stay Updated",
      desc: "Get real-time notifications on Slack, email, and in-app.",
      tag: "pink",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
    },
    {
      id: "card-6",
      num: "06",
      title: "Stay Accountable",
      desc: "Automated alerts and audits keep everything transparent and on track.",
      tag: "indigo",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="orcaflo-container animate__animated animate__fadeIn">
      {/* SECTION 1: HERO */}
      <section className="orcaflo-hero-section">
        <div className="hero-backdrop-pillar" />
        <div className="orcaflo-hero-left animate__animated animate__fadeInLeft">
          <h1 className="orcaflo-title">Orkaflo</h1>

          <p className="orcaflo-subtitle">
            Orkaflo is built for SMBs, startups, and lean teams who want structured project
            management without the integration overhead or the learning curve.
          </p>
        </div>

        <div className="orcaflo-hero-right animate__animated animate__fadeInRight">
          <div className="mascot-wrapper">
            <img src={orcaflo_logo} alt="Orkaflo Logo" className="orcaflo-logo-img" />
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <nav className="orcaflo-breadcrumbs" aria-label="Breadcrumb">
          <Link to={pageRoutes.Home} onClick={scrollToTop}>
            Home
          </Link>
          <span className="breadcrumb-separator">/</span>
          <Link to={pageRoutes.Marketplace} onClick={scrollToTop}>
            Marketplace
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Orkaflo</span>
        </nav>
      </section>

      {/* SECTION 2: WHAT IS ORKAFLO? */}
      <section className="orcaflo-what-is-section">
        <div className="what-is-container">
          <div className="what-is-text animate__animated animate__fadeInUp">
            <div className="what-is-header-row">
              <div className="title-grey-box" aria-hidden="true" />
              <h2 className="outline-title">
                What is
                <br />
                <span className="orcaflo-outline-row">
                  Orkaflo?
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>

            {/* In mobile, mockup is displayed between title and description */}
            <div className="what-is-mockup-mobile">
              <div className="orcaflo-layered-mockup">
                <object data={orcaflo_hero1_mob} type="image/svg+xml" aria-label="Orkaflo Dashboard View" className="orcaflo-mockup-bg" style={{ pointerEvents: 'none' }} />
                <object data={orcaflo_hero2_mob} type="image/svg+xml" aria-label="Orkaflo Ticket & Milestone View" className="orcaflo-mockup-fg" style={{ pointerEvents: 'none' }} />
              </div>
            </div>

            <p className="what-is-p">
              We built{" "}
              <a
                href="https://www.orkaflo.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="brand-accent"
              >
                <strong className="brand-accent">Orkaflo</strong>
              </a>{" "}
              so teams can create a
              ticket in a single click, run projects in Waterfall or Scrum from the same tool, and
              stay updated without ever leaving Slack or email. The interface is intuitive enough
              that non-technical teams can onboard themselves, and an AI Strategist Agent handles
              the heavy lifting — breaking down tasks, writing acceptance criteria, and keeping
              everything aligned.
            </p>

            <p className="what-is-p">
              Modern tech teams use agile or scrum across multiple platforms. Orkaflo brings the most
              relevant information from these systems into a unified view designed for fast
              execution. Without cluttering individual developer dashboards, leads get a
              clearer picture of performance, velocity trends, and cross-platform dependencies.
            </p>
          </div>

          <div className="what-is-mockup animate__animated animate__fadeInRight">
            <div className="orcaflo-layered-mockup">
              <img
                src={orcaflo_hero1}
                alt="Orkaflo Dashboard View"
                className="orcaflo-mockup-bg"
              />
              <img
                src={orcaflo_hero2}
                alt="Orkaflo Ticket & Milestone View"
                className="orcaflo-mockup-fg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: ONE PLATFORM. EVERY METHODOLOGY. ZERO LEARNING CURVE. */}
      <section className="orcaflo-methodology-section">
        <div className="methodology-container">
          <div className="methodology-header">
            <h2 className="methodology-main-title">
              One Platform. Every Methodology.{" "}
              <span className="methodology-subtitle">Zero Learning Curve.</span>
            </h2>
            <p className="methodology-desc">
              Orkaflo simplifies project management with one-click ticketing, flexible Scrum or
              Waterfall workflows, and effortless Slack and email updates.
            </p>
          </div>

          <div className="methodology-cards-grid">
            {featureCards.map((card, idx) => (
              <div key={idx} className="methodology-card">
                <span className="card-index">{card.num}</span>
                <h3 className="card-title">{card.title}</h3>
                <p className="card-summary">{card.desc}</p>
                <ul className="card-bullets">
                  {card.bullets.map((bullet, bIdx) => (
                    <li key={bIdx}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: FROM TICKET TO DELIVERY, IN ONE STRUCTURED FLOW */}
      <section className="orcaflo-flow-section">
        <div className="flow-container">
          <div className="flow-header">
            <h2 className="flow-main-title">
              From Ticket to Delivery, in{" "}
              <span className="flow-subtitle">One Structured Flow</span>
            </h2>
          </div>

          {/* Desktop Radial/Interconnected Layout matching Figma */}
          <div className="flow-layout-desktop">
            {/* SVG Connecting Lines Behind Cards (6 lines for all 6 cards, finishing before cards) */}
            <svg className="flow-connections-svg" viewBox="0 0 1080 680" preserveAspectRatio="none">
              {/* 1. Card 01 (Top Left) to Center Mascot */}
              <line x1="449" y1="208" x2="490" y2="267" stroke="#818cf8" strokeWidth="2" strokeDasharray="6 6" />
              {/* 2. Center Mascot to Card 02 (Top Right) */}
              <line x1="590" y1="267" x2="631" y2="208" stroke="#818cf8" strokeWidth="2" strokeDasharray="6 6" />
              {/* 3. Card 06 (Middle Left) to Center Mascot (pure horizontal) */}
              <line x1="338" y1="340" x2="470" y2="340" stroke="#818cf8" strokeWidth="2" strokeDasharray="6 6" />
              {/* 4. Center Mascot to Card 03 (Middle Right) (pure horizontal) */}
              <line x1="610" y1="340" x2="742" y2="340" stroke="#818cf8" strokeWidth="2" strokeDasharray="6 6" />
              {/* 5. Card 05 (Bottom Left) to Center Mascot */}
              <line x1="449" y1="472" x2="490" y2="413" stroke="#818cf8" strokeWidth="2" strokeDasharray="6 6" />
              {/* 6. Center Mascot to Card 04 (Bottom Right) */}
              <line x1="590" y1="413" x2="631" y2="472" stroke="#818cf8" strokeWidth="2" strokeDasharray="6 6" />
            </svg>

            {/* Central Orkaflo Hub */}
            <div className="flow-center-hub">
              <img src={orcaflo_logo} alt="Orkaflo" className="hub-mascot-img" />
              <span className="hub-label">Orkaflo</span>
            </div>

            {/* Individual Posited Cards (matching Figma radial coordinates) */}
            {flowCards.map((card) => (
              <div key={card.id} className={`flow-card flow-card-${card.tag} flow-card-pos-${card.num}`}>
                <div className="card-top-row">
                  <span className="card-num-pill">{card.num}</span>
                  <span className="card-icon-box">{card.icon}</span>
                </div>
                <h4 className="flow-card-title">{card.title}</h4>
                <p className="flow-card-desc">{card.desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile Stacked Flow Sequence */}
          <div className="flow-layout-mobile">
            {flowCards.map((card) => (
              <div key={card.id} className={`flow-card flow-card-${card.tag}`}>
                <div className="card-top-row">
                  <span className="card-num-pill">{card.num}</span>
                  <span className="card-icon-box">{card.icon}</span>
                </div>
                <h4 className="flow-card-title">{card.title}</h4>
                <p className="flow-card-desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: FROM TOOL SPRAWL TO ONE-CLICK MANAGEMENT */}
      <section className="orcaflo-sprawl-section">
        <div className="sprawl-container">
          <div className="sprawl-header">
            <h2 className="sprawl-main-title">
              From Tool Sprawl to{" "}
              <span className="sprawl-subtitle">One-Click</span>
              <br />
              <span className="sprawl-subtitle">Management</span>
            </h2>
          </div>

          <div className="sprawl-cards-grid">
            {/* Card 1: BEFORE ORKAFLO */}
            <div className="sprawl-card before-card">
              <h3 className="sprawl-card-title">BEFORE ORKAFLO</h3>
              <ul className="sprawl-list">
                <li>
                  <svg className="status-icon icon-cross" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2.2" />
                    <path d="M15 9L9 15M9 9L15 15" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Multiple tools stitched via integrations</span>
                </li>
                <li>
                  <svg className="status-icon icon-cross" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2.2" />
                    <path d="M15 9L9 15M9 9L15 15" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>One rigid methodology forced on every project</span>
                </li>
                <li>
                  <svg className="status-icon icon-cross" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2.2" />
                    <path d="M15 9L9 15M9 9L15 15" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Steep learning curve for non-technical teams</span>
                </li>
                <li>
                  <svg className="status-icon icon-cross" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2.2" />
                    <path d="M15 9L9 15M9 9L15 15" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Fragmented updates across scattered channels</span>
                </li>
              </ul>
            </div>

            {/* Card 2: AFTER ORKAFLO */}
            <div className="sprawl-card after-card">
              <h3 className="sprawl-card-title">AFTER ORKAFLO</h3>
              <ul className="sprawl-list">
                <li>
                  <svg className="status-icon icon-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>One platform, ready in a single click</span>
                </li>
                <li>
                  <svg className="status-icon icon-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>Waterfall and Scrum, side by side</span>
                </li>
                <li>
                  <svg className="status-icon icon-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>Intuitive UI, fast self-onboarding</span>
                </li>
                <li>
                  <svg className="status-icon icon-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>Real-time notifications sent everywhere</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: CALLOUT CTA */}
      <section className="orcaflo-cta-callout-section">
        <div className="cta-callout-container">
          <div className="cta-callout-left">
            <h2 className="cta-heading">
              Ready to Manage Every Project, <br className="cta-desktop-br" />
              Every Methodology, in <span className="cta-highlight">One Click?</span>
            </h2>
          </div>

          <div className="cta-callout-right">
            <p className="cta-desc">
              See how Orkaflo helps SMBs and startups plan, track, and deliver work — in
              Waterfall or Scrum, with an AI Strategist Agent doing the planning, and zero
              third-party tools required.
            </p>
            <a
              href="https://www.orkaflo.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-action-btn"
            >
              Book a demo
            </a>
          </div>
        </div>
      </section>

      {/* PRE-FOOTER CTA */}
      <HomePageFooter page="OrkaFlo" />

      {/* GLOBAL FOOTER */}
      <Footer />
    </div>
  );
};

export default OrcaFlo;
